import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, FormField, Select } from '@/shared/components';
import { toApiError, useApi, type PropertyResponse } from '@/shared/api';
import { propertyKeys } from '../queries';

export function ApartmentObjectAssignment({ property }: { property: PropertyResponse }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [targetGroupId, setTargetGroupId] = useState(property.propertyGroupId ?? '');
  const { data: groups = [] } = useQuery({
    queryKey: ['property-groups'],
    queryFn: api.propertyGroups.list,
  });
  const move = useMutation({
    mutationFn: () => api.propertyGroups.moveProperty(property.id, targetGroupId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['property-groups'] }),
        queryClient.invalidateQueries({ queryKey: propertyKeys.detail(property.id) }),
        queryClient.invalidateQueries({ queryKey: propertyKeys.lists() }),
      ]);
      toast.success('Apartment wurde dem Objekt zugeordnet');
    },
    onError: async (error) => {
      const apiError = await toApiError(error);
      toast.error(apiError.detail ?? apiError.title);
    },
  });
  const current = groups.find((group) => group.id === property.propertyGroupId);

  return (
    <section className="border-line bg-card shadow-card mb-6 rounded-lg border p-6">
      <h2 className="text-ink font-serif text-xl">Objektzuordnung</h2>
      <p className="text-ink-soft mt-1 text-sm">
        Die Adresse gehört zum Objekt. Verschiebe das Apartment, wenn es zu einer anderen Adresse
        gehört.
      </p>
      {current && (
        <p className="text-ink mt-3 text-sm">
          Aktuell: {current.name} · {current.address.street}, {current.address.postalCode}{' '}
          {current.address.city}
        </p>
      )}
      <div className="mt-4 flex items-end gap-3">
        <FormField label="Zielobjekt" className="min-w-0 flex-1">
          <Select value={targetGroupId} onChange={(event) => setTargetGroupId(event.target.value)}>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} — {group.address.street}, {group.address.city}
              </option>
            ))}
          </Select>
        </FormField>
        <Button
          type="button"
          variant="ghost"
          loading={move.isPending}
          disabled={!targetGroupId || targetGroupId === property.propertyGroupId}
          onClick={() => move.mutate()}
        >
          Verschieben
        </Button>
      </div>
    </section>
  );
}
